import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';

export type MemoryLevel = 'l1' | 'l2' | 'l3';

export interface MemoryRecordDto {
  id: string;
  level: MemoryLevel;
  key: string;
  valueText: string;
  importance: number;
  pinned?: boolean;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Создание записи памяти по явному пользовательскому пути «Запомнить» из чата.
 * Основной путь создания остаётся агентским — инструмент `write_memory` после
 * подтверждения пользователя; этот client обслуживает ручное добавление факта.
 */
export interface CreateMemoryInput {
  valueText: string;
  level?: Extract<MemoryLevel, 'l2' | 'l3'>;
  importance?: number;
  metadata?: { dealId?: string; contactId?: string } | null;
}

/**
 * Правка уже сохранённого факта. Запись в основном заводит агент инструментом
 * `write_memory` с подтверждением пользователя; дополнительно пользователь
 * может завести факт явным путём «Запомнить» из чата (createMemoryRecord).
 * Ключ и уровень не меняются — это адрес факта и его природа, а не формулировка.
 */
export interface UpdateMemoryInput {
  valueText?: string;
  importance?: number;
  pinned?: boolean;
  expectedUpdatedAt: string;
}

export interface ListRecentMemoriesResponse {
  scope: 'deal' | 'contact' | 'workspace';
  count: number;
  items: MemoryRecordDto[];
}

export interface SearchL3MemoriesResponse {
  query: string;
  count: number;
  items: Array<MemoryRecordDto & { score: number }>;
}

export async function listRecentMemories(
  level: MemoryLevel,
  limit = 20,
): Promise<ListRecentMemoriesResponse> {
  const params = new URLSearchParams({
    scope: 'workspace',
    level,
    limit: String(limit),
  });
  return apiGet<ListRecentMemoriesResponse>(`/memory/recent?${params.toString()}`);
}

export async function searchL3Memories(
  query: string,
  limit = 10,
): Promise<SearchL3MemoriesResponse> {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });
  return apiGet<SearchL3MemoriesResponse>(`/memory/search?${params.toString()}`);
}

export async function createMemoryRecord(input: CreateMemoryInput): Promise<MemoryRecordDto> {
  return apiPost<MemoryRecordDto, CreateMemoryInput>('/memory', input);
}

export async function updateMemoryRecord(
  memoryId: string,
  input: UpdateMemoryInput,
): Promise<MemoryRecordDto> {
  return apiPatch<MemoryRecordDto, UpdateMemoryInput>(`/memory/${memoryId}`, input);
}

export async function forgetMemoryRecord(
  memoryId: string,
  expectedUpdatedAt?: string,
): Promise<void> {
  await apiDelete<void, { expectedUpdatedAt: string }>(
    `/memory/${memoryId}`,
    expectedUpdatedAt === undefined ? undefined : { expectedUpdatedAt },
  );
}
